import { isSolidFramingFloor } from "building-generation/elements/floor/floor";
import type { Building } from "building-types/building/buildingTypes";
import { JoistPlacement } from "building-types/floors/floorJoistPlacementTypes";
import type { GeneratedBuildingEntityHandle } from "building-types/handles/generatedEntityHandleTypes";
import type { GeneratedFramedFloorAssembly } from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { isVoidFloorAssemblyOverride } from "doc/elements/floor/floorAssembly";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { textSizeInView } from "doc/plans/primitives/textPrimitive";
import { aabb2Move } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { polyLine2AABB2, polyLine2Centroid, polyLine2Length, polyLine2Move, polyLine2Rotate } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3ToPolyLine2IgnoreZ } from "geom/polyLine3";
import type { IndexAABB2 } from "geom/rtree";
import {
  collectPolylinesFromFloorSystemFraming,
  collectPolylinesFromFramedFloorAssembly,
  floorFramingPolylinesNew,
} from "lib/house/floorFramingPolylines";
import type { Label } from "lib/house/plans/label";
import { labelNew, tagFontSize } from "lib/house/plans/label";
import RBush from "rbush";
import { makeSheetGeometryFromLabels } from "./planLabels";
import type { PlanTagCoordinates } from "./planTags";
import { planTagCoordinatesByLine } from "./planTags";

const INCH_TO_FEET = 1 / 12;

function isValidFramedFloorAssembly(building: Building, assembly: GeneratedFramedFloorAssembly, levelId: string) {
  if (assembly.levelId !== levelId) {
    return false;
  }
  const floorEntity = building.horizontalAssemblyFloors[assembly.parentFloorId];
  if (!floorEntity || floorEntity.disabled) {
    return false;
  }
  if (isVoidFloorAssemblyOverride(floorEntity)) {
    return false;
  }
  return true;
}

function appendSheetGeometryFromFramedFloorAssemblies(
  building: Building,
  levelId: string,
  result: SheetGeometry[],
): void {
  const { framedFloorAssemblies } = building;
  if (!framedFloorAssemblies) {
    return;
  }

  for (const boundaryId in framedFloorAssemblies) {
    const assembly = framedFloorAssemblies[boundaryId];

    if (!assembly || !isValidFramedFloorAssembly(building, assembly, levelId)) {
      continue;
    }

    const polylines = floorFramingPolylinesNew();
    collectPolylinesFromFramedFloorAssembly(assembly.framing, polylines);

    const allPolylines = [
      ...polylines.rims,
      ...polylines.joists,
      ...polylines.beams,
      ...polylines.noggins,
      ...polylines.headers,
      ...polylines.trimmers,
      ...polylines.tailJoists,
    ];

    for (const { path, railId } of allPolylines) {
      const handle: GeneratedBuildingEntityHandle = {
        type: "GENERATED_ENTITY",
        entity: { entityType: "LUMBER", id: railId },
      };
      result.push(sheetPolylineGeometryNew({ handle, paths: [path] }));
    }
  }
}

function appendPlanPolylinesFromFloorSystem(
  building: Building,
  levelId: string,
  noSettings: boolean,
  rims: PolyLine2[],
  joists: PolyLine2[],
  noggins: PolyLine2[],
): void {
  const { planset: planSettings } = building.settings;
  const { generateFramingPlan } = planSettings;
  const floorSystem = building.floorSystems[levelId];

  if (!noSettings && (!generateFramingPlan || !floorSystem)) {
    return;
  }

  const polylines = floorFramingPolylinesNew();
  for (const floors of floorSystem.roomClassFloors) {
    for (const floor of floors) {
      if (!isSolidFramingFloor(floor)) {
        continue;
      }
      for (const framing of floor.floorFraming) {
        collectPolylinesFromFloorSystemFraming(framing.joists, polylines);
      }
    }
  }
  rims.push(...polylines.rims.map(({ path }) => path));
  joists.push(...polylines.joists.map(({ path }) => path));
  noggins.push(...polylines.noggins.map(({ path }) => path));
}

export function makeSheetGeometryFromFloorFraming({
  building,
  levelId,
  noSettings,
}: PlanGenerationContext): SheetGeometry[] {
  const { levels } = building;
  const result: SheetGeometry[] = [];

  /** @TODO - appendPlanPolylinesFromFloorSystem will be removed when we completely migrate to using the new floor system */
  const floorSystemRims: PolyLine2[] = [];
  const floorSystemJoists: PolyLine2[] = [];
  const floorSystemNoggins: PolyLine2[] = [];
  appendPlanPolylinesFromFloorSystem(
    building,
    levelId,
    !!noSettings,
    floorSystemRims,
    floorSystemJoists,
    floorSystemNoggins,
  );

  if (floorSystemRims.length > 0) {
    result.push(sheetPolylineGeometryNew({ handle: levels[levelId].handle, paths: floorSystemRims }));
  }
  if (floorSystemJoists.length > 0) {
    result.push(sheetPolylineGeometryNew({ handle: levels[levelId].handle, paths: floorSystemJoists }));
  }
  if (floorSystemNoggins.length > 0) {
    result.push(sheetPolylineGeometryNew({ handle: levels[levelId].handle, paths: floorSystemNoggins }));
  }

  appendSheetGeometryFromFramedFloorAssemblies(building, levelId, result);

  return result;
}

function lengthToNearestCeilWholeNumber(len: number) {
  return Math.ceil(len);
}

function formattedNameLabel(length: number, inchToFeet: number, name: string) {
  return `${lengthToNearestCeilWholeNumber(length * inchToFeet)}'-${name}`;
}

export function makeSheetGeometryFromFloorFramingLabels({
  building,
  levelId,
  noSettings,
}: PlanGenerationContext): SheetGeometry[] {
  const floorFramingLabels = makeFloorFramingLabels(building, levelId, noSettings);
  return makeSheetGeometryFromLabels({
    handle: building.levels[levelId].handle,
    labels: floorFramingLabels,
    noSettings,
  });
}

export function makeFloorFramingLabels(building: Building, levelId: string, noSettings = true) {
  const { planset: planSettings } = building.settings;
  const { paperSize } = planSettings;

  const tagRTree = new RBush<AABB2>();
  const exteriorDimsRTree = new RBush<AABB2>();
  const framingLabels: Label[] = [];
  const fontSize = tagFontSize(paperSize);

  /** @TODO - appendFloorSystemFramingLabels will be removed when we completely migrate to using the new floor system */
  appendFloorSystemFramingLabels(building, levelId, noSettings, fontSize, tagRTree, exteriorDimsRTree, framingLabels);
  appendFramedFloorAssemblyLabels(building, levelId, fontSize, tagRTree, exteriorDimsRTree, framingLabels);
  return framingLabels;
}

function appendFloorSystemFramingLabels(
  building: Building,
  levelId: string,
  noSettings: boolean,
  fontSize: SheetInches,
  tagRTree: RBush<AABB2>,
  exteriorDimsRTree: RBush<AABB2>,
  framingLabels: Label[],
): void {
  const { planset: planSettings } = building.settings;
  const { generateFramingPlan, labelFramingElements } = planSettings;

  if (!noSettings && (!generateFramingPlan || !labelFramingElements)) {
    return;
  }

  const floorSystem = building.floorSystems[levelId];
  if (!floorSystem) {
    return;
  }

  for (const floors of floorSystem.roomClassFloors) {
    for (const floor of floors) {
      if (!isSolidFramingFloor(floor)) {
        continue;
      }
      const { floorFraming } = floor;

      for (let i = 0; i < floorFraming.length; i++) {
        const { joists: joistPieces } = floorFraming[i];

        const {
          railsByType,
          profileDescriptor: { annotationLabel },
        } = joistPieces;

        const rimRails = railsByType[JoistPlacement.RIM];
        if (rimRails) {
          for (let j = 0, l = rimRails.length; j < l; j++) {
            const { rail: rimRail } = rimRails[j];
            const rimRailPoly2 = polyLine3ToPolyLine2IgnoreZ(rimRail);
            const length = polyLine2Length(rimRailPoly2);
            const nameLabel = formattedNameLabel(length, INCH_TO_FEET, annotationLabel);
            addFloorFramingLabel(rimRailPoly2, nameLabel, fontSize, tagRTree, exteriorDimsRTree, framingLabels);
          }
        }
        const floorRails = railsByType[JoistPlacement.FLOOR];
        if (floorRails) {
          for (let j = 0, l = floorRails.length; j < l; j++) {
            const { rail: joist } = floorRails[j];
            const joistRailPoly2 = polyLine3ToPolyLine2IgnoreZ(joist);
            const length = polyLine2Length(joistRailPoly2);
            const nameLabel = formattedNameLabel(length, INCH_TO_FEET, annotationLabel);
            addFloorFramingLabel(joistRailPoly2, nameLabel, fontSize, tagRTree, exteriorDimsRTree, framingLabels);
          }
        }
        const nogginRails = railsByType[JoistPlacement.NOGGIN];
        if (nogginRails) {
          for (let j = 0, l = nogginRails.length; j < l; j++) {
            const { rail: noggin } = nogginRails[j];
            const nogginRailPoly2 = polyLine3ToPolyLine2IgnoreZ(noggin);
            const length = polyLine2Length(nogginRailPoly2);
            const nameLabel = formattedNameLabel(length, INCH_TO_FEET, annotationLabel);
            addFloorFramingLabel(nogginRailPoly2, nameLabel, fontSize, tagRTree, exteriorDimsRTree, framingLabels);
          }
        }
      }
    }
  }
}

function appendFramedFloorAssemblyLabels(
  building: Building,
  levelId: string,
  fontSize: SheetInches,
  tagRTree: RBush<AABB2>,
  exteriorDimsRTree: RBush<AABB2>,
  framingLabels: Label[],
): void {
  const { framedFloorAssemblies } = building;
  if (!framedFloorAssemblies) {
    return;
  }

  for (const boundaryId in framedFloorAssemblies) {
    const assembly = framedFloorAssemblies[boundaryId];

    if (!assembly || !isValidFramedFloorAssembly(building, assembly, levelId)) {
      continue;
    }

    for (const member of assembly.framing.flat()) {
      const {
        rails,
        profileDescriptor: { annotationLabel },
      } = member;
      for (let r = 0; r < rails.length; r++) {
        const { rail } = rails[r];
        const railPoly2 = polyLine3ToPolyLine2IgnoreZ(rail);
        const length = polyLine2Length(railPoly2);
        const nameLabel = formattedNameLabel(length, INCH_TO_FEET, annotationLabel);
        addFloorFramingLabel(railPoly2, nameLabel, fontSize, tagRTree, exteriorDimsRTree, framingLabels);
      }
    }
  }
}

function addFloorFramingLabel(
  closedLoopFramingOutline: PolyLine2,
  frameLabel: string,
  fontSize: SheetInches,
  tagRTree: RBush<AABB2>,
  exteriorDimsRTree: RBush<AABB2>,
  framingLabels: Label[],
) {
  const coords = planTagCoordinatesByLine(closedLoopFramingOutline);
  const labelSize = textSizeInView(frameLabel, fontSize) as [number, number];
  const { perimeter, aabb, midX, midY, baseOffset, collisionOffset } = positionTagDeprecated({
    coords,
    tagRTree,
    exteriorDimsRTree,
    labelSize,
    tagOffset: -5, // maintain legacy position @TODO - reevaluate this value
  });
  // look for collisions with other tags
  const overlap = tagRTree.collides(aabb);

  // update the aabb based on the collision result
  if (overlap) {
    const aabbOffset = collisionOffset - baseOffset;
    aabb2Move(aabb, coords.normalX * aabbOffset, coords.normalY * aabbOffset);
    polyLine2Move(perimeter, coords.normalX * aabbOffset, coords.normalY * aabbOffset);
  }

  const { angle: textRotationRads } = coords;

  framingLabels.push(labelNew({ text: frameLabel, fontSize, centerX: midX, centerY: midY, textRotationRads }));

  // insert the tag aabb into the RTree
  tagRTree.insert(aabb as IndexAABB2);
}

interface PositionTagsArgsDeprecated {
  coords: PlanTagCoordinates;
  tagOffset?: number;
  labelSize: [number, number];
  tagRTree: RBush<AABB2>;
  exteriorDimsRTree: RBush<AABB2>;
}

/** @deprecated */
function positionTagDeprecated({
  coords,
  labelSize,
  tagRTree,
  tagOffset = 0,
  exteriorDimsRTree,
}: PositionTagsArgsDeprecated) {
  const [labelSizeX, labelSizeY] = labelSize;
  const baseOffset = -tagOffset;
  const dimCollisionOffset = 4;
  const tagCollisionOffset = -labelSizeY * 0.75;
  let collisionOffset = 0;

  // generate the base AABB
  const perimeter = makeTagLabelOutlineDeprecated(coords.centerX, coords.centerY, labelSizeX, labelSizeY, coords.angle);
  const aabb = polyLine2AABB2(perimeter);

  aabb2Move(aabb, coords.normalX * baseOffset, coords.normalY * baseOffset);
  polyLine2Move(perimeter, coords.normalX * baseOffset, coords.normalY * baseOffset);

  // look for collisions with other tags
  const overlap = tagRTree.collides(aabb);

  // update the aabb based on the collision result
  if (overlap) {
    collisionOffset += tagCollisionOffset;
    aabb2Move(aabb, coords.normalX * tagCollisionOffset, coords.normalY * tagCollisionOffset);
    polyLine2Move(perimeter, coords.normalX * tagCollisionOffset, coords.normalY * tagCollisionOffset);
  } else {
    // look for collisions with exterior dims
    const collision = exteriorDimsRTree.collides(aabb);

    // update the aabb based on the collision result
    if (collision) {
      collisionOffset += dimCollisionOffset;
      aabb2Move(aabb, coords.normalX * dimCollisionOffset, coords.normalY * dimCollisionOffset);
      polyLine2Move(perimeter, coords.normalX * dimCollisionOffset, coords.normalY * dimCollisionOffset);
    }
  }

  // insert the tag
  const [midX, midY] = ensureDefined(polyLine2Centroid(perimeter));

  return {
    aabb,
    perimeter,
    midX,
    midY,
    baseOffset,
    collisionOffset,
  };
}

/**  @deprecated  */
function makeTagLabelOutlineDeprecated(
  centerX: number,
  centerY: number,
  labelSizeX: number,
  labelSizeY: number,
  angle: number,
): PolyLine2 {
  const halfLabelSizeX = labelSizeX * 0.5;
  const halfLabelSizeY = labelSizeY * 0.5;

  const labelOutline = polyLine2Rotate(
    polyLine2New([
      centerX - halfLabelSizeX,
      centerY - halfLabelSizeY,
      centerX + halfLabelSizeX,
      centerY - halfLabelSizeY,
      centerX + halfLabelSizeX,
      centerY + halfLabelSizeY,
      centerX - halfLabelSizeX,
      centerY + halfLabelSizeY,
      centerX - halfLabelSizeX,
      centerY - halfLabelSizeY,
    ]),
    angle,
    centerX,
    centerY,
  );

  return labelOutline;
}
